<template>
  <div class="property-analysis-modal">
    <!-- Title Bar -->
    <div class="title-bar">
      <div class="title-content">
        <h3>Property Analysis</h3>
        <select v-model="selectedYear" @change="loadData" class="year-selector">
          <option v-for="yr in availableYears" :key="yr" :value="yr">
            {{ yr }}
          </option>
        </select>
        <label class="prior-year-toggle">
          <input type="checkbox" v-model="showPriorYear" />
          Show {{ selectedYear - 1 }}
        </label>
      </div>
      <div class="title-buttons">
        <button class="refresh-btn" @click="printTable" title="Print">
          <i class="fas fa-print"></i>
        </button>
        <button
          class="refresh-btn"
          @click="exportToExcel"
          title="Export to Excel"
        >
          <i class="fas fa-file-excel"></i>
        </button>
        <button class="refresh-btn" @click="loadData" title="Refresh Data">
          <i class="fas fa-sync-alt"></i>
        </button>
        <button class="close-btn" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>
    </div>

    <!-- Table Container -->
    <div class="table-body">
      <div v-if="loading" class="loading-indicator">
        <i class="fas fa-spinner fa-spin"></i> Loading property analysis...
      </div>
      <div v-if="error" class="error-message">
        <i class="fas fa-exclamation-triangle"></i> {{ error }}
      </div>

      <div v-if="!loading && !error && rows.length" class="table-scroll">
        <table class="analysis-table">
          <thead>
            <tr class="year-header-row" v-if="showPriorYear">
              <th colspan="2"></th>
              <th :colspan="8" class="year-group-header current-year-header">
                {{ selectedYear }}
              </th>
              <th :colspan="8" class="year-group-header prior-year-header">
                {{ selectedYear - 1 }}
              </th>
              <th></th>
            </tr>
            <tr>
              <th class="col-address" @click="sortBy('address')">
                Address
                <i
                  v-if="sortField === 'address'"
                  :class="
                    sortDir === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'
                  "
                ></i>
              </th>
              <th class="col-parcel">Parcel</th>
              <!-- Current year columns -->
              <th class="col-num" @click="sortBy('current.rent')">Rent</th>
              <th class="col-num" @click="sortBy('current.gas')">Gas</th>
              <th class="col-num" @click="sortBy('current.water')">Water</th>
              <th class="col-num" @click="sortBy('current.electric')">Elec.</th>
              <th class="col-num" @click="sortBy('current.maintenance')">
                Maint.
              </th>
              <th class="col-num" @click="sortBy('current.propTax')">
                PropTax
              </th>
              <th
                class="col-num col-total"
                @click="sortBy('current.totalExpense')"
              >
                Total Exp
              </th>
              <th class="col-num col-net" @click="sortBy('current.net')">
                Net
                <i
                  v-if="sortField === 'current.net'"
                  :class="
                    sortDir === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'
                  "
                ></i>
              </th>
              <!-- Prior year columns (conditional) -->
              <template v-if="showPriorYear">
                <th class="col-num col-prior" @click="sortBy('prior.rent')">
                  Rent
                </th>
                <th class="col-num col-prior" @click="sortBy('prior.gas')">
                  Gas
                </th>
                <th class="col-num col-prior" @click="sortBy('prior.water')">
                  Water
                </th>
                <th class="col-num col-prior" @click="sortBy('prior.electric')">
                  Elec.
                </th>
                <th
                  class="col-num col-prior"
                  @click="sortBy('prior.maintenance')"
                >
                  Maint.
                </th>
                <th class="col-num col-prior" @click="sortBy('prior.propTax')">
                  PropTax
                </th>
                <th
                  class="col-num col-prior col-total"
                  @click="sortBy('prior.totalExpense')"
                >
                  Total Exp
                </th>
                <th
                  class="col-num col-prior col-net"
                  @click="sortBy('prior.net')"
                >
                  Net
                  <i
                    v-if="sortField === 'prior.net'"
                    :class="
                      sortDir === 'asc' ? 'fas fa-sort-up' : 'fas fa-sort-down'
                    "
                  ></i>
                </th>
              </template>
              <th class="col-num col-yoy" @click="sortBy('yoyChange')">
                YOY Chg
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, idx) in sortedRows"
              :key="row.propertyId || idx"
              :class="{ zebra: idx % 2 === 1 }"
            >
              <td class="col-address">{{ row.address }}</td>
              <td class="col-parcel">{{ row.parcel }}</td>
              <td class="col-num">{{ fmt(row.current.rent) }}</td>
              <td class="col-num">{{ fmt(row.current.gas) }}</td>
              <td class="col-num">{{ fmt(row.current.water) }}</td>
              <td class="col-num">{{ fmt(row.current.electric) }}</td>
              <td
                class="col-num drill-cell"
                :class="{ clickable: row.current.maintenance }"
                @click="
                  row.current.maintenance &&
                  openMaintenanceDetail(row, selectedYear)
                "
                :title="
                  row.current.maintenance
                    ? 'Click to drill through to line items'
                    : ''
                "
              >
                {{ fmt(row.current.maintenance) }}
              </td>
              <td class="col-num">{{ fmt(row.current.propTax) }}</td>
              <td class="col-num col-total">
                {{ fmt(row.current.totalExpense) }}
              </td>
              <td class="col-num col-net" :class="netClass(row.current.net)">
                {{ fmt(row.current.net) }}
              </td>
              <template v-if="showPriorYear">
                <td class="col-num col-prior">{{ fmt(row.prior.rent) }}</td>
                <td class="col-num col-prior">{{ fmt(row.prior.gas) }}</td>
                <td class="col-num col-prior">{{ fmt(row.prior.water) }}</td>
                <td class="col-num col-prior">{{ fmt(row.prior.electric) }}</td>
                <td
                  class="col-num col-prior drill-cell"
                  :class="{ clickable: row.prior.maintenance }"
                  @click="
                    row.prior.maintenance &&
                    openMaintenanceDetail(row, selectedYear - 1)
                  "
                  :title="
                    row.prior.maintenance
                      ? 'Click to drill through to line items'
                      : ''
                  "
                >
                  {{ fmt(row.prior.maintenance) }}
                </td>
                <td class="col-num col-prior">{{ fmt(row.prior.propTax) }}</td>
                <td class="col-num col-prior col-total">
                  {{ fmt(row.prior.totalExpense) }}
                </td>
                <td
                  class="col-num col-prior col-net"
                  :class="netClass(row.prior.net)"
                >
                  {{ fmt(row.prior.net) }}
                </td>
              </template>
              <td class="col-num col-yoy" :class="yoyClass(row.yoyChange)">
                {{ fmt(row.yoyChange) }}
              </td>
            </tr>
          </tbody>
          <tfoot v-if="totals">
            <tr class="totals-row">
              <td class="col-address"><strong>TOTAL</strong></td>
              <td class="col-parcel"></td>
              <td class="col-num">
                <strong>{{ fmt(totals.current.rent) }}</strong>
              </td>
              <td class="col-num">
                <strong>{{ fmt(totals.current.gas) }}</strong>
              </td>
              <td class="col-num">
                <strong>{{ fmt(totals.current.water) }}</strong>
              </td>
              <td class="col-num">
                <strong>{{ fmt(totals.current.electric) }}</strong>
              </td>
              <td class="col-num">
                <strong>{{ fmt(totals.current.maintenance) }}</strong>
              </td>
              <td class="col-num">
                <strong>{{ fmt(totals.current.propTax) }}</strong>
              </td>
              <td class="col-num col-total">
                <strong>{{ fmt(totals.current.totalExpense) }}</strong>
              </td>
              <td class="col-num col-net" :class="netClass(totals.current.net)">
                <strong>{{ fmt(totals.current.net) }}</strong>
              </td>
              <template v-if="showPriorYear">
                <td class="col-num col-prior">
                  <strong>{{ fmt(totals.prior.rent) }}</strong>
                </td>
                <td class="col-num col-prior">
                  <strong>{{ fmt(totals.prior.gas) }}</strong>
                </td>
                <td class="col-num col-prior">
                  <strong>{{ fmt(totals.prior.water) }}</strong>
                </td>
                <td class="col-num col-prior">
                  <strong>{{ fmt(totals.prior.electric) }}</strong>
                </td>
                <td class="col-num col-prior">
                  <strong>{{ fmt(totals.prior.maintenance) }}</strong>
                </td>
                <td class="col-num col-prior">
                  <strong>{{ fmt(totals.prior.propTax) }}</strong>
                </td>
                <td class="col-num col-prior col-total">
                  <strong>{{ fmt(totals.prior.totalExpense) }}</strong>
                </td>
                <td
                  class="col-num col-prior col-net"
                  :class="netClass(totals.prior.net)"
                >
                  <strong>{{ fmt(totals.prior.net) }}</strong>
                </td>
              </template>
              <td class="col-num col-yoy" :class="yoyClass(totals.yoyChange)">
                <strong>{{ fmt(totals.yoyChange) }}</strong>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- Maintenance drill-through popup -->
    <div
      v-if="drillOpen"
      class="drill-overlay"
      @click.self="closeMaintenanceDetail"
    >
      <div class="drill-modal">
        <div class="drill-title-bar">
          <div class="drill-title-content">
            <h3>
              Maintenance Detail &mdash; {{ drillContext.address }}
              <small>({{ drillContext.year }})</small>
            </h3>
          </div>
          <div class="drill-title-buttons">
            <button
              class="refresh-btn"
              @click="printDrillTable"
              title="Print"
              :disabled="!drillRows.length"
            >
              <i class="fas fa-print"></i>
            </button>
            <button
              class="refresh-btn"
              @click="exportDrillToExcel"
              title="Export to Excel"
              :disabled="!drillRows.length"
            >
              <i class="fas fa-file-excel"></i>
            </button>
            <button class="close-btn" @click="closeMaintenanceDetail">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
        <div class="drill-body">
          <div v-if="drillLoading" class="loading-indicator">
            <i class="fas fa-spinner fa-spin"></i> Loading line items...
          </div>
          <div v-else-if="drillError" class="error-message">
            <i class="fas fa-exclamation-triangle"></i> {{ drillError }}
          </div>
          <div v-else-if="!drillRows.length" class="error-message">
            No maintenance line items found.
          </div>
          <div v-else class="table-scroll">
            <table class="analysis-table drill-table">
              <thead>
                <tr>
                  <th class="sortable" @click="drillSortBy('date')">
                    Date
                    <i
                      v-if="drillSortField === 'date'"
                      :class="
                        drillSortDir === 'asc'
                          ? 'fas fa-sort-up'
                          : 'fas fa-sort-down'
                      "
                    ></i>
                  </th>
                  <th>Property</th>
                  <th>Account</th>
                  <th>Class</th>
                  <th>Name / Payee</th>
                  <th>Description / Memo</th>
                  <th>Check #</th>
                  <th class="col-num sortable" @click="drillSortBy('amount')">
                    Amount
                    <i
                      v-if="drillSortField === 'amount'"
                      :class="
                        drillSortDir === 'asc'
                          ? 'fas fa-sort-up'
                          : 'fas fa-sort-down'
                      "
                    ></i>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(r, idx) in sortedDrillRows"
                  :key="(r.btId || 0) + ':' + (r.lId || 0) + ':' + idx"
                  :class="{ zebra: idx % 2 === 1, split: r.isSplitChild }"
                >
                  <td>{{ fmtDate(r.date) }}</td>
                  <td>{{ r.propertyAddress }}</td>
                  <td>{{ r.account }}</td>
                  <td>{{ r.class }}</td>
                  <td>{{ r.name || '' }}</td>
                  <td>{{ r.memo || r.description || '' }}</td>
                  <td>{{ r.checkNumber || '' }}</td>
                  <td class="col-num">{{ fmt(r.amount) }}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="totals-row">
                  <td colspan="7">
                    <strong>TOTAL ({{ drillRows.length }} rows)</strong>
                  </td>
                  <td class="col-num">
                    <strong>{{ fmt(drillTotal) }}</strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import axios from '@/axiosConfig';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

export default {
  name: 'PropertyAnalysis',
  props: {
    userRole: {
      type: Number,
      default: null,
    },
  },
  emits: ['close'],
  setup() {
    const loading = ref(false);
    const error = ref(null);
    const rows = ref([]);
    const totals = ref(null);
    const showPriorYear = ref(false);

    const now = new Date();
    const selectedYear = ref(now.getFullYear());

    const availableYears = (() => {
      const cy = now.getFullYear();
      const years = [];
      for (let y = cy; y >= 2024; y--) {
        years.push(y);
      }
      return years;
    })();

    const sortField = ref('address');
    const sortDir = ref('asc');

    const sortBy = (field) => {
      if (sortField.value === field) {
        sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
      } else {
        sortField.value = field;
        sortDir.value = field === 'address' ? 'asc' : 'desc';
      }
    };

    const getNestedValue = (obj, path) => {
      return path.split('.').reduce((o, k) => (o || {})[k], obj);
    };

    const sortedRows = computed(() => {
      const data = [...rows.value];
      const field = sortField.value;
      const dir = sortDir.value === 'asc' ? 1 : -1;
      data.sort((a, b) => {
        const va = getNestedValue(a, field);
        const vb = getNestedValue(b, field);
        if (typeof va === 'string') return dir * va.localeCompare(vb);
        return dir * ((va || 0) - (vb || 0));
      });
      return data;
    });

    const fmt = (val) => {
      if (!val && val !== 0) return '';
      if (val === 0) return '-';
      const num = Math.round(val);
      const abs = Math.abs(num).toLocaleString('en-US');
      return num < 0 ? `(${abs})` : abs;
    };

    const netClass = (val) => {
      if (val > 0) return 'positive';
      if (val < 0) return 'negative';
      return '';
    };

    const yoyClass = (val) => {
      if (val > 0) return 'positive';
      if (val < 0) return 'negative';
      return '';
    };

    const loadData = async () => {
      loading.value = true;
      error.value = null;
      rows.value = [];
      totals.value = null;

      try {
        const response = await axios.get(
          `/api/financial-summary/property-analysis/${selectedYear.value}`,
        );
        const data = response.data;
        rows.value = data.rows || [];
        totals.value = data.totals || null;
      } catch (err) {
        console.error('Error loading property analysis:', err);
        error.value = 'Failed to load property analysis data.';
      } finally {
        loading.value = false;
      }
    };

    const printTable = () => {
      const modal = document.querySelector('.property-analysis-modal');
      if (!modal) return;
      const table = modal.querySelector('table.analysis-table');
      if (!table) return;

      const yearLabel = showPriorYear.value
        ? `${selectedYear.value} vs ${selectedYear.value - 1}`
        : `${selectedYear.value}`;
      const title = `Property Analysis - ${yearLabel}`;

      const printWindow = window.open('', '_blank');
      if (!printWindow) return;
      printWindow.document.open();
      printWindow.document.write(`
        <html>
          <head>
            <title>${title}</title>
            <style>
              @page { size: landscape; margin: 0.4in 0.25in; }
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 10px;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              h1 {
                font-size: 1.1em;
                margin: 0 0 8px 0;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                font-size: 8pt;
              }
              th, td {
                border: 1px solid #ccc;
                padding: 3px 5px;
                text-align: right;
                vertical-align: top;
                white-space: nowrap;
              }
              th {
                background: #f0f0f0 !important;
                font-weight: 600;
              }
              th i { display: none; }
              th.col-address, td.col-address,
              th.col-parcel, td.col-parcel {
                text-align: left;
              }
              .year-header-row th { text-align: center; }
              .year-group-header { background: #e8e0ec !important; }
              .prior-year-header { background: #e0e8ec !important; }
              tbody tr.zebra td { background: #f7f7f7 !important; }
              .totals-row td {
                border-top: 2px solid #999 !important;
                background: #eee !important;
                font-weight: bold;
              }
              .negative { color: #b00020; }
              .positive { color: #006400; }
            </style>
          </head>
          <body onload="window.print(); window.close();">
            <h1>${title}</h1>
            ${table.outerHTML}
          </body>
        </html>
      `);
      printWindow.document.close();
    };

    const exportToExcel = () => {
      const data = sortedRows.value.map((row) => {
        const out = {
          Address: row.address,
          Parcel: row.parcel,
          Rent: row.current.rent,
          Gas: row.current.gas,
          Water: row.current.water,
          Electric: row.current.electric,
          Maintenance: row.current.maintenance,
          PropTax: row.current.propTax,
          'Total Exp': row.current.totalExpense,
          Net: row.current.net,
        };
        if (showPriorYear.value) {
          out['Prior Rent'] = row.prior.rent;
          out['Prior Gas'] = row.prior.gas;
          out['Prior Water'] = row.prior.water;
          out['Prior Electric'] = row.prior.electric;
          out['Prior Maintenance'] = row.prior.maintenance;
          out['Prior PropTax'] = row.prior.propTax;
          out['Prior Total Exp'] = row.prior.totalExpense;
          out['Prior Net'] = row.prior.net;
          out['YoY Change'] = row.yoyChange;
        }
        return out;
      });

      if (totals.value) {
        const t = {
          Address: 'TOTAL',
          Parcel: '',
          Rent: totals.value.current.rent,
          Gas: totals.value.current.gas,
          Water: totals.value.current.water,
          Electric: totals.value.current.electric,
          Maintenance: totals.value.current.maintenance,
          PropTax: totals.value.current.propTax,
          'Total Exp': totals.value.current.totalExpense,
          Net: totals.value.current.net,
        };
        if (showPriorYear.value) {
          t['Prior Rent'] = totals.value.prior.rent;
          t['Prior Gas'] = totals.value.prior.gas;
          t['Prior Water'] = totals.value.prior.water;
          t['Prior Electric'] = totals.value.prior.electric;
          t['Prior Maintenance'] = totals.value.prior.maintenance;
          t['Prior PropTax'] = totals.value.prior.propTax;
          t['Prior Total Exp'] = totals.value.prior.totalExpense;
          t['Prior Net'] = totals.value.prior.net;
          t['YoY Change'] = totals.value.yoyChange;
        }
        data.push(t);
      }

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Property Analysis');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      saveAs(
        new Blob([wbout], { type: 'application/octet-stream' }),
        `PropertyAnalysis_${selectedYear.value}.xlsx`,
      );
    };

    onMounted(() => {
      loadData();
    });

    // ----------------------------------------------------------
    // Maintenance drill-through state + handlers
    // ----------------------------------------------------------
    const drillOpen = ref(false);
    const drillLoading = ref(false);
    const drillError = ref(null);
    const drillRows = ref([]);
    const drillContext = ref({ propertyId: null, address: '', year: null });
    const drillSortField = ref('date');
    const drillSortDir = ref('asc');

    const drillTotal = computed(() =>
      drillRows.value.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0),
    );

    const sortedDrillRows = computed(() => {
      const data = [...drillRows.value];
      const field = drillSortField.value;
      const dir = drillSortDir.value === 'asc' ? 1 : -1;
      data.sort((a, b) => {
        if (field === 'amount') {
          return (
            dir * ((parseFloat(a.amount) || 0) - (parseFloat(b.amount) || 0))
          );
        }
        // date: compare via Date or fallback string
        const da = a.date ? new Date(a.date).getTime() : 0;
        const db = b.date ? new Date(b.date).getTime() : 0;
        if (!isNaN(da) && !isNaN(db)) return dir * (da - db);
        return dir * String(a.date || '').localeCompare(String(b.date || ''));
      });
      return data;
    });

    const drillSortBy = (field) => {
      if (drillSortField.value === field) {
        drillSortDir.value = drillSortDir.value === 'asc' ? 'desc' : 'asc';
      } else {
        drillSortField.value = field;
        drillSortDir.value = field === 'date' ? 'asc' : 'desc';
      }
    };

    const fmtDate = (val) => {
      if (!val) return '';
      const d = new Date(val);
      if (isNaN(d.getTime())) return String(val).slice(0, 10);
      return d.toISOString().slice(0, 10);
    };

    const openMaintenanceDetail = async (row, yr) => {
      if (!row || !row.propertyId) return;
      drillOpen.value = true;
      drillLoading.value = true;
      drillError.value = null;
      drillRows.value = [];
      drillSortField.value = 'date';
      drillSortDir.value = 'asc';
      drillContext.value = {
        propertyId: row.propertyId,
        address: row.address,
        year: yr,
      };
      try {
        const resp = await axios.get(
          `/api/financial-summary/property-analysis/maintenance-detail/${yr}/${row.propertyId}`,
        );
        drillRows.value = resp.data.rows || [];
      } catch (err) {
        console.error('Error loading maintenance detail:', err);
        drillError.value = 'Failed to load maintenance line items.';
      } finally {
        drillLoading.value = false;
      }
    };

    const closeMaintenanceDetail = () => {
      drillOpen.value = false;
      drillRows.value = [];
      drillError.value = null;
    };

    const exportDrillToExcel = () => {
      if (!drillRows.value.length) return;
      const data = sortedDrillRows.value.map((r) => ({
        Date: fmtDate(r.date),
        Property: r.propertyAddress,
        Account: r.account,
        Class: r.class,
        Name: r.name || '',
        Description: r.description || '',
        Memo: r.memo || '',
        'Check #': r.checkNumber || '',
        Amount: r.amount,
        'BT Id': r.btId,
        'Ledger Id': r.lId,
      }));
      data.push({
        Date: '',
        Property: '',
        Account: '',
        Class: '',
        Name: '',
        Description: '',
        Memo: 'TOTAL',
        'Check #': '',
        Amount: drillTotal.value,
        'BT Id': '',
        'Ledger Id': '',
      });
      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Maintenance Detail');
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const safeAddr = (drillContext.value.address || 'property').replace(
        /[^a-z0-9]+/gi,
        '_',
      );
      saveAs(
        new Blob([wbout], { type: 'application/octet-stream' }),
        `MaintenanceDetail_${safeAddr}_${drillContext.value.year}.xlsx`,
      );
    };

    const printDrillTable = () => {
      if (!drillRows.value.length) return;
      const modal = document.querySelector('.drill-modal');
      if (!modal) return;
      const table = modal.querySelector('table.drill-table');
      if (!table) return;

      const title = `Maintenance Detail - ${drillContext.value.address} (${drillContext.value.year})`;

      const printWindow = window.open('', '_blank');
      if (!printWindow) return;
      printWindow.document.open();
      printWindow.document.write(`
        <html>
          <head>
            <title>${title}</title>
            <style>
              @page { size: landscape; margin: 0.4in 0.25in; }
              body {
                font-family: Arial, sans-serif;
                margin: 0;
                padding: 10px;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              h1 {
                font-size: 1.1em;
                margin: 0 0 8px 0;
              }
              table {
                width: 100%;
                border-collapse: collapse;
                font-size: 8pt;
              }
              th, td {
                border: 1px solid #ccc;
                padding: 3px 5px;
                text-align: left;
                vertical-align: top;
              }
              th {
                background: #f0f0f0 !important;
                font-weight: 600;
              }
              th i { display: none; }
              th.col-num, td.col-num { text-align: right; }
              tbody tr.zebra td { background: #f7f7f7 !important; }
              tbody tr.split td { font-style: italic; color: #444; }
              .totals-row td {
                border-top: 2px solid #999 !important;
                background: #eee !important;
                font-weight: bold;
              }
            </style>
          </head>
          <body onload="window.print(); window.close();">
            <h1>${title}</h1>
            ${table.outerHTML}
          </body>
        </html>
      `);
      printWindow.document.close();
    };

    return {
      loading,
      error,
      rows,
      totals,
      selectedYear,
      availableYears,
      showPriorYear,
      sortField,
      sortDir,
      sortBy,
      sortedRows,
      fmt,
      netClass,
      yoyClass,
      loadData,
      printTable,
      exportToExcel,
      // drill-through
      drillOpen,
      drillLoading,
      drillError,
      drillRows,
      drillContext,
      drillTotal,
      drillSortField,
      drillSortDir,
      sortedDrillRows,
      drillSortBy,
      fmtDate,
      openMaintenanceDetail,
      closeMaintenanceDetail,
      exportDrillToExcel,
      printDrillTable,
    };
  },
};
</script>

<style scoped>
.property-analysis-modal {
  position: relative;
  width: 95vw;
  max-width: 1500px;
  height: 85vh;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
}

.title-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #a080a8;
  color: #fff;
  padding: 8px 15px;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  font-size: 1.4em;
  flex-shrink: 0;
}

.title-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.title-content h3 {
  margin: 0;
  color: #fff;
  font-size: 1em;
  font-weight: 600;
}

.year-selector {
  padding: 4px 8px;
  font-size: 0.9em;
  border: 1px solid #ccc;
  border-radius: 4px;
  background: #fff;
  color: #333;
  cursor: pointer;
}

.year-selector:hover {
  border-color: #907793;
}

.year-selector:focus {
  outline: none;
  border-color: #907793;
  box-shadow: 0 0 3px rgba(144, 119, 147, 0.5);
}

.prior-year-toggle {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.65em;
  color: #fff;
  cursor: pointer;
  user-select: none;
}

.prior-year-toggle input {
  cursor: pointer;
}

.title-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-shrink: 0;
}

.refresh-btn,
.close-btn {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #e1dede;
  color: #eee;
  background: #907793;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.refresh-btn {
  margin-right: 30px;
}

.refresh-btn:hover,
.close-btn:hover {
  background: #7a6b7d;
  color: #fff;
  border-color: #fff;
}

.refresh-btn i,
.close-btn i {
  font-size: 12px;
}

.table-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 10px;
}

.loading-indicator {
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 1.1em;
}

.error-message {
  background: #ffebee;
  color: #c62828;
  padding: 12px 20px;
  border-radius: 4px;
  border: 1px solid #ef9a9a;
  margin: 10px 0;
}

.table-scroll {
  flex: 1;
  overflow: auto;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.analysis-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8em;
  font-family: Calibri, sans-serif;
  white-space: nowrap;
}

.analysis-table thead {
  position: sticky;
  top: 0;
  z-index: 2;
}

.year-header-row th {
  background: #f0f0f0;
  border-bottom: 1px solid #ccc;
  padding: 3px 6px;
  font-weight: 600;
  text-align: center;
  font-size: 0.95em;
}

.current-year-header {
  background: #e8ddef !important;
  color: #4a2d5e;
}

.prior-year-header {
  background: #f0ebe4 !important;
  color: #6b5a3e;
}

.analysis-table th {
  background: #f8f8f8;
  border-bottom: 2px solid #ccc;
  padding: 5px 8px;
  font-weight: 600;
  color: #333;
  cursor: pointer;
  user-select: none;
  text-align: right;
}

.analysis-table th:hover {
  background: #eee;
}

.analysis-table th i {
  margin-left: 3px;
  font-size: 0.8em;
}

.analysis-table th.col-address {
  text-align: left;
  min-width: 110px;
  max-width: 140px;
}

.analysis-table th.col-parcel {
  text-align: left;
  cursor: default;
  min-width: 70px;
  max-width: 90px;
}

.analysis-table td {
  padding: 3px 8px;
  border-bottom: 1px solid #f0f0f0;
  text-align: right;
  color: #333;
}

.analysis-table td.col-address {
  text-align: left;
  font-weight: 500;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.analysis-table td.col-parcel {
  text-align: left;
  color: #888;
  font-size: 0.9em;
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.col-num {
  min-width: 65px;
}

.col-total {
  border-left: 2px solid #ccc !important;
  font-weight: 600;
}

.col-net {
  font-weight: 600;
  border-right: 2px solid #ccc !important;
}

.col-prior {
  background: #faf8f5;
}

.col-yoy {
  font-weight: 600;
  border-left: 2px solid #ccc !important;
}

.zebra td {
  background: #fafafa;
}

.zebra td.col-prior {
  background: #f5f2ef;
}

.positive {
  color: #2e7d32;
}

.negative {
  color: #c62828;
}

.totals-row td {
  border-top: 2px solid #999;
  background: #f5f5f5 !important;
  padding: 6px 8px;
}

/* Drill-through clickable cell */
.drill-cell.clickable {
  cursor: pointer;
  color: #4a3a78;
  text-decoration: underline dotted;
}
.drill-cell.clickable:hover {
  background: #f0e8f4;
}

/* Drill-through popup */
.drill-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}
.drill-modal {
  width: 90vw;
  max-width: 1300px;
  height: 80vh;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.drill-title-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #a080a8;
  color: #fff;
  padding: 8px 15px;
  font-size: 1.2em;
  flex-shrink: 0;
}
.drill-title-content h3 {
  margin: 0;
  color: #fff;
  font-size: 1em;
  font-weight: 600;
}
.drill-title-content small {
  font-weight: 400;
  margin-left: 6px;
  opacity: 0.85;
}
.drill-title-buttons {
  display: flex;
  gap: 8px;
}
.drill-body {
  flex: 1;
  overflow: auto;
  padding: 10px;
  background: #fafafa;
}
.drill-table th,
.drill-table td {
  padding: 4px 8px;
  font-size: 0.92em;
  white-space: nowrap;
}
.drill-table th.sortable {
  cursor: pointer;
  user-select: none;
}
.drill-table th.sortable:hover {
  background: #ece4f0;
}
.drill-table tr.split td {
  font-style: italic;
  color: #444;
}

/* Print styles */
@media print {
  .property-analysis-modal {
    position: static;
    width: 100%;
    height: auto;
    max-width: none;
    box-shadow: none;
    border: none;
  }

  .title-buttons {
    display: none;
  }

  .table-scroll {
    overflow: visible;
    border: none;
  }

  .analysis-table {
    font-size: 7pt;
  }
}
</style>
